/**
 * ConnectivityManager - Online/Offline Detection and Management
 * 
 * Handles connectivity detection and routing saves based on online/offline status.
 * Online: Direct database saves
 * Offline: Working copy staging
 */

export interface ConnectivityState {
  isOnline: boolean;
  lastOnlineTime: Date | null;
  lastOfflineTime: Date | null;
  connectionType: 'online' | 'offline' | 'unknown';
}

export interface ConnectivityListener {
  onOnline: () => void;
  onOffline: () => void;
  onConnectivityChange: (state: ConnectivityState) => void;
}

export class ConnectivityManager {
  private state: ConnectivityState;
  private listeners: ConnectivityListener[] = [];
  private pingInterval: NodeJS.Timeout | null = null;
  private apiBaseUrl: string;
  private authToken: string;

  constructor(apiBaseUrl: string, authToken: string) {
    this.apiBaseUrl = apiBaseUrl;
    this.authToken = authToken;
    
    this.state = {
      isOnline: navigator.onLine,
      lastOnlineTime: navigator.onLine ? new Date() : null,
      lastOfflineTime: !navigator.onLine ? new Date() : null,
      connectionType: navigator.onLine ? 'online' : 'offline'
    };

    this.setupEventListeners();
    this.startConnectivityPing();
    
    console.log('[ConnectivityManager] Initialized with state:', this.state);
  }

  /**
   * Get current connectivity state
   */
  getState(): ConnectivityState {
    return { ...this.state };
  }

  /**
   * Check if currently online
   * CRITICAL FIX: Prioritize browser connectivity over server WebSocket status
   * WebSocket failures shouldn't mark the system as offline if browser is online
   */
  isOnline(): boolean {
    // If browser reports online, trust it over server connectivity issues
    if (navigator.onLine) {
      return true;
    }
    return this.state.isOnline;
  }

  /**
   * Check if currently offline
   */
  isOffline(): boolean {
    return !this.state.isOnline;
  }

  /**
   * Add connectivity listener
   */
  addListener(listener: ConnectivityListener): void {
    this.listeners.push(listener);
  }

  /**
   * Remove connectivity listener
   */
  removeListener(listener: ConnectivityListener): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  /**
   * Setup browser connectivity event listeners
   */
  private setupEventListeners(): void {
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }

  /**
   * Handle online event
   */
  private handleOnline(): void {
    console.log('[ConnectivityManager] 🌐 Browser reports ONLINE');
    this.updateState(true);
    
    // Verify with server ping
    this.verifyConnectivity().then(isActuallyOnline => {
      if (isActuallyOnline) {
        console.log('[ConnectivityManager] ✅ Server connectivity confirmed');
        this.notifyListeners('online');
      } else {
        console.log('[ConnectivityManager] ❌ Server unreachable despite browser online status');
        this.updateState(false);
      }
    });
  }

  /**
   * Handle offline event
   */
  private handleOffline(): void {
    console.log('[ConnectivityManager] 📴 Browser reports OFFLINE');
    this.updateState(false);
    this.notifyListeners('offline');
  }

  /**
   * Update connectivity state
   */
  private updateState(isOnline: boolean): void {
    const wasOnline = this.state.isOnline;
    
    this.state.isOnline = isOnline;
    this.state.connectionType = isOnline ? 'online' : 'offline';
    
    if (isOnline && !wasOnline) {
      this.state.lastOnlineTime = new Date();
    } else if (!isOnline && wasOnline) {
      this.state.lastOfflineTime = new Date();
    }

    this.notifyListeners('change');
  }

  /**
   * Notify all listeners of connectivity changes
   */
  private notifyListeners(eventType: 'online' | 'offline' | 'change'): void {
    this.listeners.forEach(listener => {
      try {
        if (eventType === 'online') {
          listener.onOnline();
        } else if (eventType === 'offline') {
          listener.onOffline();
        }
        listener.onConnectivityChange(this.getState());
      } catch (error) {
        console.error('[ConnectivityManager] Listener error:', error);
      }
    });
  }

  /**
   * Verify actual server connectivity with ping
   */
  private async verifyConnectivity(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(`${this.apiBaseUrl}/api/v1/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      console.warn('[ConnectivityManager] Server ping failed:', error);
      return false;
    }
  }

  /**
   * Start periodic connectivity ping
   */
  private startConnectivityPing(): void {
    // Ping server every 30 seconds to verify connectivity
    this.pingInterval = setInterval(async () => {
      const isActuallyOnline = await this.verifyConnectivity();
      const browserOnline = navigator.onLine;
      
      // CRITICAL FIX: Only go offline if BOTH browser AND server report offline
      // Don't switch to offline mode just because of temporary server issues
      if (browserOnline && !isActuallyOnline) {
        console.log(`[ConnectivityManager] Server unreachable but browser online - staying ONLINE for saves`);
        // Keep current online state, don't switch to offline mode
        return;
      }
      
      if (isActuallyOnline !== this.state.isOnline) {
        console.log(`[ConnectivityManager] Connectivity state change. Browser: ${browserOnline}, Server: ${isActuallyOnline}`);
        this.updateState(isActuallyOnline);
        
        if (isActuallyOnline) {
          this.notifyListeners('online');
        } else {
          this.notifyListeners('offline');
        }
      }
    }, 30000);
  }

  /**
   * Stop connectivity monitoring
   */
  destroy(): void {
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
    
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    
    this.listeners = [];
    console.log('[ConnectivityManager] Destroyed');
  }
}
